import React from 'react';
import { Box, Skeleton, Grid } from '@mui/material';
import BasicContentBox from '@root/components/v2/content/BasicContentBox';
import ContentBox from '@root/components/v2/content/ContentBox';
import { SeverityCountCardSkeleton } from './SeverityCountCard';

export const AlertComponentsSkeleton = () => <SeverityCountCardSkeleton title="Alerts" />;

export const ActiveThreatSkeleton = () => {
  return (
    <BasicContentBox title={'Active threat'} style={{ minHeight: '375px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box display="flex" flexDirection="column" alignItems="center" height="100%" overflow="hidden">
          <Grid
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}>
            {Array.from(new Array(5)).map((_, i) => (
              <Grid
                key={i}
                sx={{
                  borderRadius: '5px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <Grid sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: '#ffffff',
                      boxShadow: '0 1px 4px 0 rgba(30,41,59,0.08)',
                      m: 2,
                      padding: '10px',
                      mb: 0,
                    }}>
                    <Skeleton width="300px" height="30px" />
                    <Skeleton width="30px" />
                  </Box>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </BasicContentBox>
  );
};

export const AssetOverviewSkeleton = () => {
  return (
    <BasicContentBox
      title="Asset Overview"
      style={{
        height: '375px',
      }}>
      <Box display="flex" flexDirection="column" alignItems="center" height="100%" overflow="hidden">
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            width: '100%',
          }}>
          <Box width="100%" display="flex" padding={`0px 10px`} flexDirection="column">
            <Skeleton variant="rounded" width={`100%`} height="290px" />
            <Box width="100%" paddingTop={'15px'} display="flex" gap="10px" flexDirection="row" justifyContent="center">
              <Box
                width="100%"
                gap="8px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="row">
                <Skeleton variant="rounded" width="15px" height="15px" />
                <Skeleton variant="rounded" width="90px" />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Box>
    </BasicContentBox>
  );
};

export const AttackPathSkeleton = () => <SeverityCountCardSkeleton title="Attack Paths Leading to Data Assets" />;

export const LiveSecretsSkeleton = () => {
  return (
    <BasicContentBox
      title={
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <span>Live Secrets</span>
        </Box>
      }
      style={{
        height: '375px',
      }}>
      <Box display="flex" flexDirection="column" alignItems="center" height="100%" overflow="hidden">
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            width: '100%',
          }}>
          <Box width="100%" display="flex" padding={`0px 10px`} flexDirection="column">
            <Skeleton variant="rounded" width={`100%`} height="290px" />
            <Box width="100%" paddingTop={'15px'} display="flex" gap="10px" flexDirection="row" justifyContent="center">
              {Array.from(new Array(3)).map((_, i) => (
                <Box
                  key={i}
                  width="100%"
                  gap="8px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="row">
                  <Skeleton variant="rounded" width="15px" height="15px" />
                  <Skeleton variant="rounded" width="90px" />
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
      </Box>
    </BasicContentBox>
  );
};

export const SensitiveDataSkeleton = () => {
  return (
    <BasicContentBox
      subtitle={'Asset Containing the sensitive data by asset type'}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <div>{'Sensitive Data'}</div>
        </div>
      }
      style={{ height: '375px', overflow: 'none !important', fontFamily: 'Manrope' }}>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          padding: '15px',
          zIndex: '999',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}>
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}>
          <Box
            sx={{
              height: '100%',
              padding: '10px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
            }}>
            {Array.from(new Array(5)).map((_, idx) => (
              <Box key={idx}>
                <Skeleton variant="circular" height={120} width={120} />
              </Box>
            ))}
          </Box>
          <Box></Box>
        </Box>
      </Box>
    </BasicContentBox>
  );
};

export const EntityFindingsSkeleton = () => {
  return (
    <BasicContentBox
      title="Entity Findings"
      titleContainerStyles={{ borderBottom: '0px' }}
      style={{ height: '300px', overflow: 'hidden' }}>
      <Box sx={{ width: '100%', height: '100%', padding: '0 16px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', height: '100%' }}>
          {/* Header skeleton */}
          <Box sx={{ display: 'flex', gap: '20px', mb: 1 }}>
            <Skeleton variant="rectangular" width="30%" height={30} />
            <Skeleton variant="rectangular" width="20%" height={30} />
            <Skeleton variant="rectangular" width="30%" height={30} />
          </Box>
          {/* Row skeletons */}
          {Array.from(new Array(5)).map((_, i) => (
            <Skeleton key={i} variant="rectangular" width="100%" height={40} />
          ))}
        </Box>
      </Box>
    </BasicContentBox>
  );
};

export const TopActiveActorSkeleton = () => {
  return (
    <ContentBox
      title="Top Active Actors"
      containerClassName={'border-box'}
      paddingMode={'content'}
      titleSize="medium"
      style={{ height: '300px' }}>
      <Box height={'100%'}>
        <Box display="flex" justifyContent="space-between" padding="10px">
          <Skeleton width="250px" height="25px" />
          <Skeleton width="300px" height="25px " />
        </Box>

        <Box display="flex" justifyContent="space-between" width="100%" height="80%" padding="10px">
          <Box display="flex" flexDirection="column" boxSizing="border-box" height="100%">
            {Array.from(new Array(10)).map((_, index) => (
              <Skeleton key={index} width="250px" height="25px" />
            ))}
          </Box>
          <Box display="flex" flexDirection="column" boxSizing="border-box" height="100%">
            {Array.from(new Array(10)).map((_, index) => (
              <Skeleton key={index} width="300px" height="25px" />
            ))}
          </Box>
        </Box>
      </Box>
    </ContentBox>
  );
};
